/* The Glow Trade — ONE add-to-order behaviour for every quantity control: catalogue cards,
 * PDP cross-sell rails and the PDP order card. Extracted from sections/tgt-wholesale-catalogue.liquid
 * (2026-09-04, PDP handoff) so the product page does not carry a second copy of the cart plumbing.
 *
 * Contract — a root carries [data-tgt-qty-root]; every control inside sits on
 * [data-tgt-card][data-variant] with:
 *   [data-tgt-qty-input]   the quantity field       [data-tgt-add]      "+ Add" (sets 1)
 *   [data-tgt-qty-dec|inc] stepper buttons         [data-tgt-qty-err]  message line
 *   [data-tgt-stepper]     stepper wrapper         [data-max]          stock cap (tracked + "deny" variants only)
 *
 * data-tgt-mode on the ROOT:
 *   "live"   (default) the input IS the cart quantity — every settled change is written.
 *   "submit"           the input is an amount; written only on [data-tgt-submit], added on top of
 *                      what the cart already holds. [data-tgt-qty-chip="6"] bumps the input.
 *
 * Optional on a submit card: [data-tgt-line-total] gets qty x data-unit-price (cents), formatted
 * with the root's data-money-format; [data-tgt-in-cart] shows the quantity already in the order.
 *
 * Entering a number (2026-09-10, owner: adding numbers on the phone was "weird"):
 *   - stepper taps and chips settle after 450 ms and write once. A TYPED number writes on change
 *     (blur, Enter, the keyboard's Done) — never per keystroke, so typing "24" no longer puts 2
 *     into the order on the way and re-renders the order bar twice.
 *   - the stock cap is applied HERE, before any request: the field is clamped to data-max and an
 *     amber note says what happened ("Set to 7 · all in stock"); it stays until the next edit. The
 *     server clamp remains the backstop and reports "Only N available" in red if it ever disagrees.
 *   - a write carries the quantity it was scheduled with, and a response never overwrites a newer
 *     tap or a field the buyer is typing in (a 500 ms round trip used to eat the second "+").
 *   - an emptied field restores the last quantity. Removing a line is "−" down to 0, never a blank.
 *   - "+ Add" moves focus into the field for fine pointers only: on a phone that popped the
 *     keyboard on every single add.
 *
 * Cart writes go to /cart/update.js with `sections`, exactly as before, so the order bar
 * (snippets/tgt-mini-cart.liquid) morphs itself from the same response via CartLinesUpdateEvent.
 */
import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';

const roots = [...document.querySelectorAll('[data-tgt-qty-root]')];

if (roots.length) {
  const SOURCE = 'tgt-qty';
  const COARSE = window.matchMedia('(pointer: coarse)').matches;
  const MSG = { only: 'Only %n available', capped: 'Set to %n · all in stock', more: 'Only %n more available',
    all: 'All %n already in your order', unavailable: 'Not available right now', cart: 'Could not update the cart' };
  for (const root of roots) {
    const el = root.querySelector('[data-tgt-qty-i18n]');
    if (el) { try { Object.assign(MSG, JSON.parse(el.textContent)); } catch (e) {} }
  }

  const cards = () => roots.flatMap((r) => [...r.querySelectorAll('[data-tgt-card][data-variant]')]);
  const rootOf = (card) => card.closest('[data-tgt-qty-root]');
  const isSubmit = (card) => rootOf(card)?.dataset.tgtMode === 'submit';
  const inputOf = (card) => card.querySelector('[data-tgt-qty-input]');
  const num = (v) => Math.max(0, parseInt(String(v ?? '').replace(/\D+/g, ''), 10) || 0);
  const inCart = (card) => num(card.dataset.inCart);
  // no data-max = untracked or "continue selling" = no cap
  const capOf = (card) => (card.dataset.max === undefined ? Infinity : num(card.dataset.max));
  // what the field may hold: the cart quantity (live) or what can still be added on top (submit)
  const limitOf = (card) => (isSubmit(card) ? Math.max(0, capOf(card) - inCart(card)) : capOf(card));

  /* ---------- money (Shopify money_format, the four standard placeholders) ---------- */
  function formatMoney(cents, format) {
    const value = (cents / 100).toFixed(2);
    const [whole, frac] = value.split('.');
    const group = (s, sep) => s.replace(/\B(?=(\d{3})+(?!\d))/g, sep);
    return format.replace(/\{\{\s*(\w+)\s*\}\}/g, (_, name) => {
      switch (name) {
        case 'amount_no_decimals': return group(whole, ',');
        case 'amount_with_comma_separator': return `${group(whole, '.')},${frac}`;
        case 'amount_no_decimals_with_comma_separator': return group(whole, '.');
        default: return `${group(whole, ',')}.${frac}`;
      }
    });
  }
  function paintTotal(card) {
    const out = card.querySelector('[data-tgt-line-total]');
    if (!out) return;
    const unit = Number(card.dataset.unitPrice);
    if (!Number.isFinite(unit)) return;
    out.textContent = formatMoney(unit * num(inputOf(card)?.value), rootOf(card).dataset.moneyFormat || '{{amount}}');
  }

  /* ---------- messages: "max" is a stock note that survives a write, "error" is a failed write ---------- */
  function say(card, text, kind) {
    const err = card.querySelector('[data-tgt-qty-err]');
    if (err) { err.hidden = false; err.textContent = text; err.dataset.kind = kind; err.classList.toggle('tgt-stepper-msg--max', kind === 'max'); }   // unhide first: a live region only announces exposed text
    const st = card.querySelector('[data-tgt-stepper]');
    st?.classList.toggle('tgt-stepper--error', kind === 'error');
    st?.classList.toggle('tgt-stepper--max', kind === 'max');
  }
  function fail(card, text) { say(card, text, 'error'); }
  function clear(card, kind) {            // no kind = everything
    const err = card.querySelector('[data-tgt-qty-err]');
    const st = card.querySelector('[data-tgt-stepper]');
    if (!kind || kind === 'error') st?.classList.remove('tgt-stepper--error');
    if (!kind || kind === 'max') st?.classList.remove('tgt-stepper--max');
    if (err && (!kind || err.dataset.kind === kind)) { err.hidden = true; delete err.dataset.kind; }
  }
  // the one place the stock cap is applied: clamps, and says what happened
  function clampInput(card, wanted) {
    const limit = limitOf(card);
    if (wanted <= limit) return wanted;
    if (!isSubmit(card)) say(card, MSG.capped.replace('%n', limit), 'max');
    else if (limit === 0) say(card, MSG.all.replace('%n', capOf(card)), 'max');
    else say(card, (inCart(card) ? MSG.more : MSG.only).replace('%n', limit), 'max');
    return limit;
  }

  /* ---------- state ---------- */
  function paintMax(card) {
    const input = inputOf(card);
    const atMax = !!input && num(input.value) >= limitOf(card);
    card.classList.toggle('is-max', atMax);
    card.querySelector('[data-tgt-qty-inc]')?.setAttribute('aria-disabled', String(atMax));
  }
  function setQty(card, q) {
    const input = inputOf(card);
    if (input) input.value = q;
    card.dataset.qty = q;
    paintMax(card);
    paintTotal(card);
  }
  // Live cards mirror the cart; submit cards keep their own amount but show what is already ordered.
  function hydrate(cart) {
    const qty = new Map(cart.items.map((i) => [i.variant_id, i.quantity]));
    for (const card of cards()) {
      const have = qty.get(Number(card.dataset.variant)) || 0;
      card.dataset.inCart = have;
      const badge = card.querySelector('[data-tgt-in-cart]');
      if (badge) { badge.textContent = badge.dataset.template.replace('%n', have); badge.hidden = have === 0; }
      const input = inputOf(card);
      if (isSubmit(card)) { paintMax(card); paintTotal(card); continue; }
      // never overwrite a field the buyer is typing in, or a change still on its way to the server
      if (input && !card.classList.contains('is-saving') && document.activeElement !== input) setQty(card, have);
    }
  }
  document.addEventListener(StandardEvents.cartLinesUpdate, (e) => {
    e.promise?.then(({ detail }) => { if (detail && detail.source !== SOURCE && detail.items) hydrate(detail); }).catch(() => {});
  });
  if (cards().some((c) => inputOf(c))) {
    fetch('/cart.js', { headers: { Accept: 'application/json' } }).then((r) => r.json()).then(hydrate).catch(() => {});
  }

  /* ---------- write: one /cart/update.js per settled change, serialised ---------- */
  const timers = new Map();
  let chain = Promise.resolve();

  // The order bar and the /cart page re-render from the same response — that is how the
  // threshold progress stays honest without any client-side price maths.
  function sectionIds() {
    return [...document.querySelectorAll('cart-items-component, [data-tgt-mini-cart]')]
      .map((n) => n.dataset.sectionId).filter(Boolean).join(',');
  }
  // Any progress bar outside the order sheet (the PDP order card) takes the fresh one with it.
  function paintProgress(sections) {
    const html = sections && sections[document.querySelector('[data-tgt-mini-cart]')?.dataset.sectionId];
    if (!html) return;
    const next = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-tgt-progress]');
    if (!next) return;
    document.querySelectorAll('[data-tgt-progress]').forEach((el) => {
      if (!el.closest('[data-tgt-mini-cart]')) el.replaceWith(next.cloneNode(true));
    });
  }

  async function push(card, wanted) {
    const vid = Number(card.dataset.variant);
    try {
      const res = await fetch(rootOf(card).dataset.cartUpdateUrl || '/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ updates: { [vid]: wanted }, sections: sectionIds() }),
      });
      const cart = await res.json();
      if (!res.ok) throw new Error(cart.description || cart.message || MSG.cart);
      const line = cart.items.find((i) => i.variant_id === vid);
      const actual = line ? line.quantity : 0;
      card.dataset.inCart = actual;
      // a newer edit is on its way (timer armed, or the intent moved on): let that one settle the field
      const stale = !isSubmit(card) && (timers.has(card) || num(card.dataset.qty) !== wanted);
      if (isSubmit(card)) hydrate(cart);
      else if (stale) paintMax(card);
      else if (document.activeElement !== inputOf(card)) setQty(card, actual);
      else { card.dataset.qty = actual; paintMax(card); }   // typing in progress: keep the field, the change event compares against the truth
      if (stale) { /* the pending write reports for itself */ }
      else if (actual === wanted) clear(card, 'error');
      else fail(card, actual ? MSG.only.replace('%n', actual) : MSG.unavailable);
      paintProgress(cart.sections);
      document.dispatchEvent(new CartLinesUpdateEvent({
        action: 'update',
        context: 'product',
        lines: [{ merchandiseId: String(vid), quantity: actual }],
        promise: Promise.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
          detail: { items: cart.items, item_count: cart.item_count, sections: cart.sections, source: SOURCE, didError: false },
        }),
      }));
    } catch (e) {
      fail(card, e.message || MSG.cart);
    } finally {
      card.classList.remove('is-saving');
      card.querySelector('[data-tgt-stepper]')?.classList.remove('tgt-stepper--saving');
    }
  }

  function saving(card) {
    card.classList.add('is-saving');
    card.querySelector('[data-tgt-stepper]')?.classList.add('tgt-stepper--saving');
  }
  // live mode: the field already holds the (clamped) quantity; settle, then write THAT quantity —
  // captured now, not re-read when the timer fires, so a digit typed meanwhile is never sent half-way
  function schedule(card) {
    const q = num(inputOf(card).value);
    saving(card);
    card.dataset.qty = q;
    paintMax(card);
    clearTimeout(timers.get(card));
    timers.set(card, setTimeout(() => { timers.delete(card); chain = chain.then(() => push(card, q)); }, 450));
  }
  function submit(card) {
    const input = inputOf(card);
    const add = clampInput(card, num(input.value));
    if (!add) return;   // nothing can be added: the note says so, the field is left alone
    input.value = add;
    paintMax(card);
    paintTotal(card);
    saving(card);
    chain = chain.then(() => push(card, inCart(card) + add));
  }

  /* ---------- events (delegated per root) ---------- */
  for (const root of roots) {
    root.addEventListener('click', (e) => {
      const card = e.target.closest('[data-tgt-card]');
      if (!card) return;
      const input = inputOf(card);

      if (e.target.closest('[data-tgt-add]')) {
        clear(card);
        const q = clampInput(card, 1);
        if (q === num(card.dataset.qty)) return;   // nothing to add (cap 0): the note is the answer
        input.value = q;
        schedule(card);
        // the button just hid itself, so focus must land somewhere: the field (selected, so typing replaces
        // the 1) on a fine pointer; on a phone the "+" button, which opens no keyboard
        if (COARSE) card.querySelector('[data-tgt-qty-inc]')?.focus({ preventScroll: true });
        else { input.focus({ preventScroll: true }); input.select(); }
        return;
      }
      if (e.target.closest('[data-tgt-submit]')) { submit(card); return; }

      // "Reorder 24 →" sets the amount and sends it in one press.
      const again = e.target.closest('[data-tgt-reorder]');
      if (again) {
        clear(card);
        setQty(card, clampInput(card, num(again.dataset.tgtReorder)));
        submit(card);
        return;
      }

      const chip = e.target.closest('[data-tgt-qty-chip]');
      const step = e.target.closest('[data-tgt-qty-dec], [data-tgt-qty-inc]');
      if (!chip && !step) return;
      const cur = num(input.value);
      const floor = isSubmit(card) ? 1 : 0;   // an amount to add is never 0; a cart line at 0 is a removal
      let next = chip ? cur + num(chip.dataset.tgtQtyChip)
        : step.hasAttribute('data-tgt-qty-inc') ? cur + 1 : Math.max(floor, cur - 1);
      clear(card);
      next = clampInput(card, next);
      if (isSubmit(card)) { setQty(card, Math.max(floor, next)); return; }
      if (next === num(card.dataset.qty)) { paintMax(card); return; }   // at the cap: no no-op write, no order-bar flicker
      input.value = next;
      schedule(card);
    });
    root.addEventListener('input', (e) => {
      if (!e.target.matches('[data-tgt-qty-input]')) return;
      const digits = e.target.value.replace(/\D+/g, '');
      if (digits !== e.target.value) e.target.value = digits;   // the phone keypad still offers "." and "-"
      const card = e.target.closest('[data-tgt-card]');
      clear(card);
      if (isSubmit(card)) paintTotal(card);
      // a live card commits a typed number on change (blur / Enter / Done), never per keystroke
    });
    root.addEventListener('change', (e) => {
      if (!e.target.matches('[data-tgt-qty-input]')) return;
      const card = e.target.closest('[data-tgt-card]');
      if (e.target.value.trim() === '') {   // an emptied field is not a decision to remove: restore
        setQty(card, isSubmit(card) ? Math.max(1, num(card.dataset.qty)) : num(card.dataset.qty));
        return;
      }
      if (isSubmit(card)) { setQty(card, Math.max(1, clampInput(card, Math.max(1, num(e.target.value))))); return; }
      const q = clampInput(card, num(e.target.value));
      e.target.value = q;
      if (q !== num(card.dataset.qty)) schedule(card);
      else paintMax(card);
    });
    root.addEventListener('keydown', (e) => {
      if (e.key !== 'Enter' || !e.target.matches('[data-tgt-qty-input]')) return;
      e.preventDefault();
      const card = e.target.closest('[data-tgt-card]');
      e.target.blur();   // commits through `change` first and closes the phone keyboard in both modes
      if (isSubmit(card)) submit(card);
    });
  }
}
